import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, AlertCircle, Check, Store, Calendar, DollarSign, ShoppingCart } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Item {
  description: string;
  quantity: number | null;
  unitPrice: number | null;
  lineTotal: number | null;
  confidence: number;
}

interface ReceiptData {
  storeName: string | null;
  storeAddress: string | null;
  date: string | null;
  time: string | null;
  currency: string | null;
  subtotal: number | null;
  tax: number | null;
  total: number | null;
  paymentMethod: string | null;
  items: Item[];
  overallConfidence: number;
}

interface ReceiptDataReviewScreenProps {
  data: ReceiptData;
  imageUrls: string[];
  onConfirm: (editedData: ReceiptData) => void;
  onBack: () => void;
}

export default function ReceiptDataReviewScreen({ 
  data, 
  imageUrls, 
  onConfirm, 
  onBack 
}: ReceiptDataReviewScreenProps) {
  const [editedData, setEditedData] = useState<ReceiptData>(data);

  const handleFieldChange = (field: keyof ReceiptData, value: any) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index: number, field: keyof Item, value: any) => {
    setEditedData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleDeleteItem = (index: number) => {
    setEditedData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleAddItem = () => {
    setEditedData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: "",
          quantity: null,
          unitPrice: null,
          lineTotal: null,
          confidence: 1.0
        }
      ]
    }));
  };

  const getConfidenceMessage = () => {
    const conf = editedData.overallConfidence;
    if (conf >= 0.8) return { icon: Check, text: "Looks good!", color: "text-success" };
    if (conf >= 0.6) return { icon: AlertCircle, text: "Please check", color: "text-warning" };
    return { icon: AlertCircle, text: "Needs review", color: "text-destructive" };
  };

  const confidence = getConfidenceMessage();
  const ConfidenceIcon = confidence.icon;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="p-4 border-b bg-card/95 backdrop-blur sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3">
          <Button onClick={onBack} variant="ghost" size="icon" className="shrink-0">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold">Check Your Receipt</h1>
            <p className="text-sm text-muted-foreground truncate">
              Make sure everything looks right
            </p>
          </div>
          <div className={`flex items-center gap-1.5 ${confidence.color} shrink-0`}>
            <ConfidenceIcon className="h-4 w-4" />
            <span className="text-sm font-medium hidden sm:inline">{confidence.text}</span>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 max-w-2xl mx-auto w-full pb-24">
          {/* Helpful tip for low confidence */}
          {editedData.overallConfidence < 0.7 && (
            <Card className="p-4 bg-accent/50 border-accent">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-accent-foreground mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <h3 className="font-semibold text-sm">Double-check the details</h3>
                  <p className="text-sm text-muted-foreground">
                    Some info might not be 100% accurate. Take a quick look before saving.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Receipt Photo */}
          {imageUrls.length > 0 && (
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <ShoppingCart className="h-4 w-4 text-primary" />
                </div>
                <h3 className="font-semibold">Your Receipt</h3>
              </div>
              <img 
                src={imageUrls[0]} 
                alt="Receipt"
                className="w-full rounded-lg border max-h-48 object-contain bg-muted"
              />
            </Card>
          )}

          {/* Store Details */}
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Store className="h-4 w-4 text-primary" />
              </div>
              <h3 className="font-semibold">Where You Shopped</h3>
            </div>
            
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="storeName" className="text-sm font-normal">Store name</Label>
                <Input
                  id="storeName"
                  value={editedData.storeName || ""}
                  onChange={(e) => handleFieldChange("storeName", e.target.value)}
                  placeholder="e.g., Costco, Walmart"
                  className="h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="storeAddress" className="text-sm font-normal">Address (optional)</Label>
                <Input
                  id="storeAddress"
                  value={editedData.storeAddress || ""}
                  onChange={(e) => handleFieldChange("storeAddress", e.target.value)}
                  placeholder="Store location"
                  className="h-11"
                />
              </div>
            </div>
          </Card>

          {/* When */}
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              <h3 className="font-semibold">When</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="date" className="text-sm font-normal">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={editedData.date || ""}
                  onChange={(e) => handleFieldChange("date", e.target.value)}
                  className="h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="time" className="text-sm font-normal">Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={editedData.time || ""}
                  onChange={(e) => handleFieldChange("time", e.target.value)}
                  className="h-11"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="space-y-1.5">
                <Label htmlFor="paymentMethod" className="text-sm font-normal">Paid with</Label>
                <Input
                  id="paymentMethod"
                  value={editedData.paymentMethod || ""}
                  onChange={(e) => handleFieldChange("paymentMethod", e.target.value)}
                  placeholder="Credit, Debit, Cash"
                  className="h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="currency" className="text-sm font-normal">Currency</Label>
                <Input
                  id="currency"
                  value={editedData.currency || "CAD"}
                  onChange={(e) => handleFieldChange("currency", e.target.value)}
                  placeholder="CAD, USD"
                  className="h-11"
                />
              </div>
            </div>
          </Card>

          {/* Money */}
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-primary" />
              </div>
              <h3 className="font-semibold">How Much</h3>
            </div>
            
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="subtotal" className="text-sm font-normal">Subtotal</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="subtotal"
                    type="number"
                    step="0.01"
                    value={editedData.subtotal ?? ""}
                    onChange={(e) => handleFieldChange("subtotal", e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="0.00"
                    className="h-11 pl-7"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="tax" className="text-sm font-normal">Tax</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="tax"
                    type="number"
                    step="0.01"
                    value={editedData.tax ?? ""}
                    onChange={(e) => handleFieldChange("tax", e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="0.00"
                    className="h-11 pl-7"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="total" className="text-sm font-medium">Total Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold">$</span>
                  <Input
                    id="total"
                    type="number"
                    step="0.01"
                    value={editedData.total ?? ""}
                    onChange={(e) => handleFieldChange("total", e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="0.00"
                    className="h-12 pl-7 font-semibold text-lg"
                  />
                </div>
              </div>
            </div>
          </Card>

        </div>
      </ScrollArea>

      {/* Actions - Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-card/95 backdrop-blur z-50 shadow-[0_-2px_12px_rgba(0,0,0,0.08)]">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <Button
              onClick={onBack}
              variant="outline"
              size="sm"
              className="h-10 px-4"
            >
              Go Back
            </Button>
            <Button
              onClick={() => onConfirm(editedData)}
              size="sm"
              className="h-10 px-6"
            >
              <Check className="h-4 w-4 mr-2" />
              Save This Receipt
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
